// Sandboxed iframe for assistant-emitted ```render blocks.
//
// Security model: srcdoc + sandbox="allow-scripts" puts the rendered HTML in an
// *opaque* origin. Scripts inside cannot read the parent's localStorage (where
// the auth token lives), cookies, IndexedDB, or DOM. Scripts are enabled only
// so the frame can postMessage its content height back for auto-sizing.
//
// Theme tokens are snapshotted into the srcdoc as CSS variables. The snapshot
// is re-taken when the parent's <html> class/data-theme changes.
//
// Title is pulled from the first <title>…</title> in the HTML and stripped
// before injection so it doesn't render as body text.
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { EyeIcon, EyeOffIcon, RetryIcon } from '../ui/Icons';

const THEME_VARS = [
  '--color-background',
  '--color-surface',
  '--color-elevated',
  '--color-divider',
  '--color-accent',
  '--color-accent-soft',
  '--color-user-bubble',
  '--color-user-bubble-text',
  '--color-text-primary',
  '--color-text-secondary',
  '--color-text-faint',
  '--color-thinking-accent',
  '--color-tool-accent',
  '--color-success-text',
  '--color-error-text',
  '--color-error-bg',
];

function readThemeVars(): string {
  const cs = getComputedStyle(document.documentElement);
  return THEME_VARS.map((n) => `${n}: ${cs.getPropertyValue(n).trim()};`).join(' ');
}

function subscribeThemeKey(cb: () => void): () => void {
  const obs = new MutationObserver(cb);
  obs.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'data-theme'],
  });
  return () => obs.disconnect();
}
function getThemeKey(): string {
  return document.documentElement.className + '|' + (document.documentElement.dataset.theme ?? '');
}

const HEIGHT_REPORTER = `<script>(function(){
  var id=window.name;
  function send(){
    var h=Math.max(document.documentElement.scrollHeight, document.body?document.body.scrollHeight:0);
    parent.postMessage({__niuRenderHeight:h,id:id},'*');
  }
  window.addEventListener('load',send);
  if(typeof ResizeObserver!=='undefined'){try{new ResizeObserver(send).observe(document.documentElement);}catch(e){}}
  setTimeout(send,30);setTimeout(send,200);
})();</script>`;

function buildSrcdoc(html: string, themeCss: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="color-scheme" content="light dark"><style>
:root{${themeCss}}
html,body{margin:0;padding:0;background:var(--color-surface);color:var(--color-text-primary);}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Inter',sans-serif;font-size:14px;line-height:1.5;padding:12px;}
a{color:var(--color-accent);}
*{box-sizing:border-box;}
img,svg,canvas,video{max-width:100%;}
table{border-collapse:collapse;}
</style></head><body>${html}${HEIGHT_REPORTER}</body></html>`;
}

const TITLE_RE = /<title\b[^>]*>([\s\S]*?)<\/title\s*>/i;

function extractTitle(html: string): { title: string | null; body: string } {
  const m = TITLE_RE.exec(html);
  if (!m) return { title: null, body: html };
  const title = m[1].trim();
  const body = html.slice(0, m.index) + html.slice(m.index + m[0].length);
  return { title: title || null, body };
}

interface Props {
  html: string;
}

export function RenderFrame({ html }: Props) {
  const id = useMemo(() => `niu-render-${Math.random().toString(36).slice(2)}`, []);
  const themeKey = useSyncExternalStore(subscribeThemeKey, getThemeKey, getThemeKey);
  const { title, body } = useMemo(() => extractTitle(html), [html]);
  // reloadToken inlined as a no-op comment so React diff sees a fresh string
  // and the iframe reloads without grabbing a ref.
  const [reloadToken, setReloadToken] = useState(0);
  const [hidden, setHidden] = useState(false);
  const srcdoc = useMemo(
    () => buildSrcdoc(`<!--r${reloadToken}-->${body}`, readThemeVars()),
    [body, themeKey, reloadToken],
  );
  const [height, setHeight] = useState(80);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (!d || typeof d !== 'object' || d.id !== id) return;
      const h = d.__niuRenderHeight;
      if (typeof h === 'number' && h > 0 && h < 4000) setHeight(Math.ceil(h));
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [id]);

  return (
    <div className="my-2 rounded-lg bg-surface overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1 text-text-secondary">
        {title ? (
          <span className="text-[12px] font-medium truncate pl-1" title={title}>
            {title}
          </span>
        ) : null}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setHidden((v) => !v)}
          className="w-[22px] h-[22px] flex items-center justify-center rounded hover:bg-divider/50 transition-colors"
          title={hidden ? 'Anzeigen' : 'Ausblenden'}
          aria-label={hidden ? 'Anzeigen' : 'Ausblenden'}
        >
          {hidden ? <EyeIcon size={12} /> : <EyeOffIcon size={12} />}
        </button>
        <button
          type="button"
          onClick={() => setReloadToken((v) => v + 1)}
          disabled={hidden}
          className="w-[22px] h-[22px] flex items-center justify-center rounded hover:bg-divider/50 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
          title="Neu laden"
          aria-label="Neu laden"
        >
          <RetryIcon size={12} />
        </button>
      </div>
      {hidden ? null : (
        <>
          <div className="h-px bg-divider" />
          <iframe
            name={id}
            sandbox="allow-scripts"
            srcDoc={srcdoc}
            title={title ?? 'Rendered output'}
            style={{ width: '100%', height, border: 0, display: 'block' }}
          />
        </>
      )}
    </div>
  );
}
